import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import apiClient from '../../api/client';

const EMPTY_LAYOUT = { tool_order: [], pinned: [], hidden: [] };
const AdminLayoutContext = createContext(null);

function normalizeLayout(layout) {
  const next = { ...EMPTY_LAYOUT, ...(layout || {}) };
  for (const key of ['tool_order', 'pinned', 'hidden']) {
    next[key] = Array.isArray(next[key]) ? next[key].filter(Boolean).map(String) : [];
  }
  return next;
}

export function AdminLayoutProvider({ children }) {
  const [layout, setLayout] = useState(EMPTY_LAYOUT);
  const [version, setVersion] = useState(0);
  const [nodeRole, setNodeRole] = useState(null);
  const saveTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    apiClient.get('/api/admin/layout')
      .then((res) => {
        if (cancelled) return;
        setLayout(normalizeLayout(res.data?.layout || res.data?.layout_json));
        setVersion(res.data?.version || 0);
      })
      .catch(() => {
        if (!cancelled) setLayout(EMPTY_LAYOUT);
      });
    return () => {
      cancelled = true;
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    apiClient.get('/api/admin/env')
      .then((res) => {
        if (cancelled) return;
        setNodeRole(res.data?.node_role || 'unknown');
      })
      .catch(() => {
        if (!cancelled) setNodeRole('unknown');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const saveLayout = useCallback((nextLayout) => {
    const normalized = normalizeLayout(nextLayout);
    setLayout(normalized);
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(async () => {
      try {
        const res = await apiClient.put('/api/admin/layout', { layout_json: normalized });
        setVersion(res.data?.version || 0);
      } catch (err) {
        console.error('Failed to save admin layout', err);
      }
    }, 500);
  }, []);

  const value = useMemo(() => ({ layout, saveLayout, version, nodeRole }), [layout, saveLayout, version, nodeRole]);
  return <AdminLayoutContext.Provider value={value}>{children}</AdminLayoutContext.Provider>;
}

export function useAdminLayout() {
  const context = useContext(AdminLayoutContext);
  if (!context) {
    return { layout: EMPTY_LAYOUT, saveLayout: () => {}, version: 0, nodeRole: null };
  }
  return context;
}
